import { useEffect, useMemo, useState } from 'react';
import {
  FontIcon,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
} from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import {
  PagedDataGrid,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import {
  getTenantSurcharges,
  type IAutomaticSurchargeRule,
} from '../../services/automaticSurchargeService';

type SurchargeRow = IAutomaticSurchargeRule & { id: number };

const locationLabel = (rule: IAutomaticSurchargeRule) =>
  rule.countryCode
    ? [rule.countryCode, rule.stateCode].filter(Boolean).join(' / ')
    : 'All locations';

const tenantLabel = (value: boolean | null) =>
  value === null ? 'Use global setting' : value ? 'Enabled' : 'Disabled';

const columns: DataGridColumn<SurchargeRow>[] = [
  { key: 'name', name: 'Name', minWidth: 180, width: 240 },
  { key: 'code', name: 'Code', minWidth: 140, width: 200 },
  {
    key: 'enabled',
    name: 'Global',
    minWidth: 90,
    width: 120,
    value: (item) => (item.enabled ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'tenantEnabled',
    name: 'This tenant',
    minWidth: 140,
    width: 170,
    value: (item) => tenantLabel(item.tenantEnabled),
  },
  {
    key: 'effectiveEnabled',
    name: 'Effective',
    minWidth: 90,
    width: 120,
    value: (item) => (item.effectiveEnabled ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'location',
    name: 'Location',
    minWidth: 120,
    width: 160,
    value: (item) => locationLabel(item),
  },
];

export default function TenantSurcharges({ tenantId }: { tenantId: number }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SurchargeRow[]>([]);
  const [selected, setSelected] = useState<SurchargeRow>();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string>();
  const [reload, setReload] = useState(0);

  const edit = (row: SurchargeRow) => {
    void navigate(`/editTenantSurcharge?id=${row.pricingItemId}`);
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoaded(false);
    setRows([]);
    setSelected(undefined);
    setError(undefined);
    getTenantSurcharges(controller)
      .then((current) => {
        if (controller.signal.aborted) return;
        setRows(
          [...current]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((rule) => ({ ...rule, id: rule.pricingItemId }))
        );
        setLoaded(true);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(getApiErrorMessage(e, 'Unable to load tenant surcharges.'));
        setLoaded(true);
      });
    return () => controller.abort();
  }, [tenantId, reload]);

  const commands: ICommandBarItem[] = useMemo(
    () => [
      {
        key: 'refresh',
        text: 'Refresh',
        icon: <FontIcon iconName="Refresh" />,
        onClick: () => {
          setSelected(undefined);
          setReload((n) => n + 1);
        },
      },
      {
        key: 'editTenantSurcharge',
        text: 'Edit',
        icon: <FontIcon iconName="Edit" />,
        disabled: !selected,
        onClick: () => {
          if (selected) edit(selected);
        },
      },
    ],
    [navigate, selected]
  );

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <Text>
        Override surcharge defaults for the selected tenant. Use global setting
        removes the override and follows future global changes. Rates remain in
        Rate overrides.
      </Text>
      {error && (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      )}
      <PagedDataGrid<SurchargeRow>
        columns={columns}
        items={rows}
        loading={!loaded}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        onSelectionChange={setSelected}
        onRowDoubleClicked={edit}
        empty={{
          title:
            'No automatic surcharges are configured. Add them in Global settings → Automatic surcharges.',
        }}
      />
    </Stack>
  );
}
